import { MarketplaceDefinition } from '../types/marketplaceDefinition';
import { apiClient } from '../api/client';

export interface ValidationRequest {
  definition_id: string;
  definition: Record<string, any>;
  validation_options: {
    test_live_pages: boolean;
    suggest_improvements: boolean;
    /** Ignored by the server as of MR 2 (Apr 2026). Suggestions are advisory;
     *  the user approves them in the UI before any definition change persists.
     *  Kept in the type for backward compatibility with the request shape. */
    auto_fix: boolean;
    check_competitors: boolean;
  };
}

/** One suggestion from the validator. Mirrors the scraper's
 *  backend/models/ai_validation.py ValidationSuggestion shape (which is
 *  itself a translation of obp_ai's richer suggestion type). */
export interface ValidationSuggestion {
  type:
    | 'broken_selector'
    | 'missing_config'
    | 'performance_optimization'
    | 'security_concern'
    | 'best_practice_violation'
    | 'add_common_headers'
    | 'add_user_agents';
  severity: 'low' | 'medium' | 'high' | 'critical';
  page?: string;
  field?: string;
  current_selector?: string;
  suggested_selector?: string;
  suggested_value?: string;
  confidence?: number;
  reason: string;
  recommendation?: string;
}

export interface ValidationResponse {
  success: boolean;
  validation_id: string;
  status: string;
  issues_found: number;
  fixes_applied: number;
  suggestions: ValidationSuggestion[];
  /** As of MR 2, this is always identical to the input definition (the
   *  server never auto-applies suggestions). Kept in the response shape for
   *  backward compatibility; callers should ignore it and use `suggestions`
   *  to drive an approve/reject UI. */
  improved_definition: Record<string, any>;
  analysis_summary: {
    total_selectors_tested: number;
    working_selectors: number;
    broken_selectors: number;
    coverage_score: number;
    reliability_score: number;
  };
}

export interface ValidationStatus {
  validation_id: string;
  status: 'started' | 'in_progress' | 'completed' | 'failed';
  progress: number;
  current_step: string;
  started_at: string;
  last_updated?: string;
  estimated_completion?: string;
  error_message?: string;
  // Optional result fields when completed
  issues_found?: number;
  fixes_applied?: number;
  suggestions?: ValidationSuggestion[];
  improved_definition?: Record<string, any>;
  analysis_summary?: {
    total_selectors_tested: number;
    working_selectors: number;
    broken_selectors: number;
    coverage_score: number;
    reliability_score: number;
  };
  results?: any;
}

export interface ValidationHistory {
  definition_id: string;
  validations: {
    validation_id: string;
    timestamp: string;
    status: string;
    issues_found: number;
    fixes_applied: number;
  }[];
}

class AIValidationService {
  constructor() {
    // Uses the main API client which handles base URL configuration
  }

  /**
   * Start AI validation of a marketplace definition
   */
  async startValidation(request: ValidationRequest): Promise<ValidationResponse> {
    try {
      // The scraper backend mounts ai_validation.router at two prefixes:
      //   - /api/ai/*               (local dev — direct access to the pod)
      //   - /api/v1/scraper/ai/*    (production — routed through the ingress)
      // The UI runs in browsers that always go through the ingress, so use
      // the /api/v1/scraper/ai prefix matching what the other UI services do.
      const response = await apiClient.post('/api/v1/scraper/ai/validate-definition', request);
      return response.data;
    } catch (error: any) {
      throw new Error(`AI validation failed: ${error.response?.statusText || error.message}`);
    }
  }

  /**
   * Get the current status of a validation process
   */
  async getValidationStatus(validationId: string): Promise<ValidationStatus> {
    try {
      const response = await apiClient.get(`/api/v1/scraper/ai/validation-status/${validationId}`, {
        timeout: 120000, // 2 minutes timeout for status checks (validation can take a while)
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Failed to get validation status: ${error.response?.statusText || error.message}`
      );
    }
  }

  /**
   * Get validation history for a definition
   */
  async getValidationHistory(definitionId: string): Promise<ValidationHistory> {
    try {
      const response = await apiClient.get(`/api/v1/scraper/ai/validation-history/${definitionId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Failed to get validation history: ${error.response?.statusText || error.message}`
      );
    }
  }

  /**
   * Cancel a running validation
   */
  async cancelValidation(validationId: string): Promise<void> {
    try {
      await apiClient.post(`/api/v1/scraper/ai/cancel-validation/${validationId}`);
    } catch (error: any) {
      throw new Error(
        `Failed to cancel validation: ${error.response?.statusText || error.message}`
      );
    }
  }

  /**
   * Poll validation status until completion
   */
  async waitForValidationCompletion(
    validationId: string,
    onProgress?: (status: ValidationStatus) => void,
    abortSignal?: AbortSignal
  ): Promise<ValidationStatus> {
    while (true) {
      // Check if cancelled
      if (abortSignal?.aborted) {
        throw new Error('Validation cancelled by user');
      }

      const status = await this.getValidationStatus(validationId);

      if (onProgress) {
        onProgress(status);
      }

      if (status.status === 'completed' || status.status === 'failed') {
        return status;
      }

      // Wait before polling again
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(resolve, 2000);
        if (abortSignal) {
          abortSignal.addEventListener('abort', () => {
            clearTimeout(timeout);
            reject(new Error('Validation cancelled by user'));
          });
        }
      });
    }
  }

  /**
   * Start validation and wait for completion
   */
  async validateDefinition(
    definition: MarketplaceDefinition,
    options: {
      test_live_pages?: boolean;
      suggest_improvements?: boolean;
      auto_fix?: boolean;
      check_competitors?: boolean;
      onProgress?: (status: ValidationStatus) => void;
      abortSignal?: AbortSignal;
    } = {}
  ): Promise<ValidationResponse> {
    const {
      test_live_pages = true,
      suggest_improvements = true,
      // Default flipped in MR 2 (server ignores this anyway since the auto-
      // apply path was removed). The UI now collects per-suggestion approve/
      // reject decisions before any change persists.
      auto_fix = false,
      check_competitors = false,
      onProgress,
      abortSignal,
    } = options;

    // Start validation
    const start = await this.startValidation({
      definition_id: definition.id || 'new',
      definition: definition.config || {},
      validation_options: {
        test_live_pages,
        suggest_improvements,
        auto_fix,
        check_competitors,
      },
    });

    // Wait for completion and get final status (which now includes results)
    const finalStatus = await this.waitForValidationCompletion(
      start.validation_id,
      onProgress,
      abortSignal
    );

    // Prefer results from status if available; otherwise fall back to start payload shape
    const issues_found = finalStatus.issues_found ?? 0;
    const fixes_applied = finalStatus.fixes_applied ?? 0;
    const suggestions = finalStatus.suggestions ?? [];
    const improved_definition = finalStatus.improved_definition ?? (definition.config || {});
    const analysis_summary = finalStatus.analysis_summary ?? start.analysis_summary;

    return {
      success: true,
      validation_id: start.validation_id,
      status: finalStatus.status,
      issues_found,
      fixes_applied,
      suggestions,
      improved_definition,
      analysis_summary,
    };
  }
}

// Export singleton instance
export const aiValidationService = new AIValidationService();
export default aiValidationService;
